import React, { useContext, useEffect, useMemo, useState } from "react";
import { MovieTaxonomyData } from "../../entry/server/movie.ts";
import { TaxonomyApiType, taxonomySupportsImage } from "../../services/video-service.ts";
import { videoRepo } from "../../repo/video-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";
import { ASSETS_BASE_URL } from "../../utils/constants.ts";

const inputClass =
  "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400";

const MAX_IMAGE_BYTES = 300 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

function assetUrl(path: string) {
  if (!path) return "";
  return path.includes("http") ? path : `${ASSETS_BASE_URL}${path.replace(/^\//, "")}`;
}

type Props = {
  type: TaxonomyApiType;
  title: string;
};

export default function TaxonomyAdminScreen({ type, title }: Props) {
  const { setMessage } = useContext(AppContext);
  const supportsImage = taxonomySupportsImage(type);
  const [items, setItems] = useState<MovieTaxonomyData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [editing, setEditing] = useState<MovieTaxonomyData | null>(null);
  const [editForm, setEditForm] = useState({ name: "", slug: "" });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState("");
  const [saving, setSaving] = useState(false);

  const imagePreviewUrl = useMemo(() => {
    if (imageFile) return URL.createObjectURL(imageFile);
    if (editing?.image) return assetUrl(editing.image);
    return "";
  }, [imageFile, editing?.image]);

  useEffect(() => {
    return () => {
      if (imageFile && imagePreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imageFile, imagePreviewUrl]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await videoRepo.getTaxonomies(type, page, searchQuery || undefined);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || `دریافت لیست ${title} ناموفق بود.` });
        setItems([]);
        return;
      }
      setItems(response.data);
      setPagination(response.pagination ?? null);
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, page, searchQuery]);

  const onSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSearchQuery(searchInput.trim());
  };

  const openEdit = (item: MovieTaxonomyData) => {
    setEditing(item);
    setEditForm({ name: item.name ?? "", slug: item.slug ?? "" });
    setImageFile(null);
    setImageError("");
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm({ name: "", slug: "" });
    setImageFile(null);
    setImageError("");
  };

  const onImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const typeOk =
      ACCEPTED_IMAGE_TYPES.includes(file.type) ||
      /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!typeOk) {
      setImageError("فقط فایل‌های JPG، JPEG، PNG یا WEBP مجاز است.");
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setImageError("حجم تصویر نباید بیشتر از ۳۰۰ مگابایت باشد.");
      return;
    }

    setImageError("");
    setImageFile(file);
  };

  const onSaveEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    if (!editForm.name.trim() || !editForm.slug.trim()) {
      setMessage({ type: "error", message: "نام و اسلاگ الزامی است." });
      return;
    }
    if (imageError) {
      setMessage({ type: "error", message: imageError });
      return;
    }

    setSaving(true);
    try {
      const res = await videoRepo.updateTaxonomy(
        type,
        editing.id,
        editForm.name.trim(),
        editForm.slug.trim(),
        supportsImage ? imageFile : null
      );
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "ذخیره تغییرات ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "تغییرات با موفقیت ذخیره شد." });
      closeEdit();
      await fetchItems();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: number) => {
    if (actionLoading !== null) return;
    if (!window.confirm("این مورد حذف شود؟")) return;

    setActionLoading(id);
    try {
      const res = await videoRepo.deleteTaxonomy(type, id);
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "حذف ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "با موفقیت حذف شد." });
      if (editing?.id === id) closeEdit();
      await fetchItems();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <section className="space-y-4">
      <AdminPageTitle title={title} />

      <form onSubmit={onSearchSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <label className="flex-1 space-y-2">
          <span className="text-sm text-slate-300">جستجو</span>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="نام یا اسلاگ..."
            className={inputClass}
          />
        </label>
        <button
          type="submit"
          className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400"
        >
          جستجو
        </button>
      </form>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست...
        </div>
      ) : !items.length ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">موردی یافت نشد.</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="min-w-full divide-y divide-slate-800 text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
                  {supportsImage ? (
                    <th className="px-4 py-3 text-right font-medium text-slate-300">تصویر</th>
                  ) : null}
                  <th className="px-4 py-3 text-right font-medium text-slate-300">نام</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">اسلاگ</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {items.map((item) => (
                  <tr key={item.id} className="transition hover:bg-slate-900">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{item.id}</td>
                    {supportsImage ? (
                      <td className="px-4 py-3">
                        {item.image ? (
                          <img
                            src={assetUrl(item.image)}
                            alt={item.name || ""}
                            className="h-10 w-10 rounded-lg border border-slate-700 object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                      </td>
                    ) : null}
                    <td className="px-4 py-3 text-slate-100">{item.name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{item.slug || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          className="rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500"
                        >
                          ویرایش
                        </button>
                        <button
                          type="button"
                          disabled={actionLoading === item.id}
                          onClick={() => void onDelete(item.id)}
                          className="rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:border-rose-600 disabled:opacity-50"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}

      {editing ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="بستن"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeEdit}
          />
          <form
            onSubmit={onSaveEdit}
            className="relative z-10 w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 shadow-2xl"
          >
            <h2 className="mb-4 text-sm font-semibold text-slate-100">ویرایش — {title}</h2>
            <div className="space-y-4">
              <label className="block space-y-2">
                <span className="text-xs text-slate-400">نام</span>
                <input
                  className={inputClass}
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </label>
              <label className="block space-y-2">
                <span className="text-xs text-slate-400">اسلاگ</span>
                <input
                  className={inputClass}
                  value={editForm.slug}
                  onChange={(e) => setEditForm((f) => ({ ...f, slug: e.target.value }))}
                  required
                />
              </label>
              {supportsImage ? (
                <div className="space-y-2">
                  <span className="text-xs text-slate-400">تصویر (اختیاری)</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={onImageChange}
                    className="block w-full text-sm text-slate-300 file:me-3 file:rounded-lg file:border-0 file:bg-indigo-500 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-indigo-400"
                  />
                  <p className="text-xs text-slate-500">فرمت‌های مجاز: JPG، JPEG، PNG، WEBP — حداکثر ۳۰۰ مگابایت</p>
                  {imageError ? <p className="text-xs text-rose-400">{imageError}</p> : null}
                  {imagePreviewUrl ? (
                    <img
                      src={imagePreviewUrl}
                      alt="پیش‌نمایش تصویر"
                      className="mt-1 h-28 w-28 rounded-lg border border-slate-700 object-cover"
                    />
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {saving ? "در حال ذخیره…" : "ذخیره"}
              </button>
              <button
                type="button"
                onClick={closeEdit}
                className="rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:border-slate-500"
              >
                انصراف
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </section>
  );
}
